import { Citrus, MapPin, Mail, Phone } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

const LANGS = ['en', 'es', 'fr', 'de', 'nl'];
const LANG_NAMES = { en: 'English', es: 'Español', fr: 'Français', de: 'Deutsch', nl: 'Nederlands' };

export const Footer = () => {
  const { t, lang, setLang } = useLanguage();
  return (
    <footer id="office" className="bg-brand-green text-brand-cream/80 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10 text-xs">
        <div className="md:col-span-3 space-y-3">
          <div className="flex items-center gap-2">
            <Citrus className="w-5 h-5 text-brand-orange" />
            <span className="font-display text-xl uppercase tracking-tight text-brand-cream">Angry Tangerine</span>
          </div>
          <p className="text-brand-cream/60 text-[11px] leading-relaxed">{t.footer.tagline}</p>
        </div>

        <div className="md:col-span-2">
          <h5 className="font-extrabold text-brand-cream uppercase tracking-[0.15em] mb-3 text-[11px]">{t.footer.navTitle}</h5>
          <ul className="space-y-2">
            <li><a href="#product" className="hover:text-brand-orange transition-colors">{t.nav.bottle}</a></li>
            <li><a href="#how-it-works" className="hover:text-brand-orange transition-colors">{t.nav.how}</a></li>
            <li><a href="#surfaces" className="hover:text-brand-orange transition-colors">{t.nav.surfaces}</a></li>
            <li><a href="#reviews" className="hover:text-brand-orange transition-colors">{t.nav.reviews}</a></li>
            <li><a href="#product" className="hover:text-brand-orange transition-colors">{t.header.buy}</a></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <h5 className="font-extrabold text-brand-cream uppercase tracking-[0.15em] mb-3 text-[11px]">{t.footer.langTitle}</h5>
          <p className="text-brand-cream/60 text-[11px] mb-3">{t.footer.langNote}</p>
          <div className="flex flex-wrap gap-x-2 gap-y-1 font-bold text-brand-cream">
            {LANGS.map((l) => (
              <button
                key={l}
                type="button"
                data-testid={`footer-lang-${l}-btn`}
                onClick={() => setLang(l)}
                className={`transition-colors ${lang === l ? 'underline text-brand-orange' : 'hover:text-brand-orange'}`}
              >
                {LANG_NAMES[l]}
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-5">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
            <h5 className="font-display text-xl uppercase text-brand-cream">{t.office.title}</h5>
            <p className="text-brand-cream/70 text-[11px] leading-relaxed">{t.office.text}</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" />
                <div>
                  <span className="block font-bold text-brand-cream">{t.office.hqTitle}</span>
                  <span className="text-brand-cream/75 text-[11px]" data-testid="office-address">{t.office.hqAddress}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-brand-orange shrink-0" />
                <div>
                  <span className="block font-bold text-brand-cream">{t.office.emailLabel}</span>
                  <a href="mailto:hello@angrytangerine.com" data-testid="contact-email-link" className="text-brand-cream/75 text-[11px] font-mono hover:text-brand-orange transition-colors">hello@angrytangerine.com</a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-brand-orange shrink-0" />
                <div>
                  <span className="block font-bold text-brand-cream">{t.office.phoneLabel}</span>
                  <a href="tel:+34912562315" data-testid="contact-phone-link" className="text-brand-cream/75 text-[11px] font-mono hover:text-brand-orange transition-colors">+34 912 562 315</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 py-4 text-center text-[11px] text-brand-cream/50" data-testid="footer-legal">
        {t.footer.rights}
      </div>

      <div
        className="font-display uppercase text-brand-cream/10 text-[13.5vw] leading-[0.75] text-center select-none pointer-events-none -mb-[4.5vw] whitespace-nowrap"
        aria-hidden="true"
      >
        Angry Tangerine
      </div>
    </footer>
  );
};
